import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import supabase from "../supabase";
import { useAuthContext } from "../contexts/AuthContext";

export interface Note {
  id: string; title: string; content_markdown: string; revision: number;
  source_message_id: string | null; source_thread_id: string | null;
  created_at: string; updated_at: string;
}
export interface NotePreview extends Omit<Note, "content_markdown"> { preview: string }
export class NotesError extends Error {
  constructor(public status: number, message: string) { super(message); }
}
interface NotesState {
  request: <T>(path?: string, options?: RequestInit) => Promise<T>;
  saved: Record<string, string | null>;
  changed: number;
  remember: (note: Note) => void;
  forget: (messageId: string) => void;
  remove: (note: Note) => Promise<void>;
}
const NotesContext = createContext<NotesState | null>(null);
export const useNotes = () => useContext(NotesContext);

function SessionNotes({ userId, children }: { userId?: string; children: React.ReactNode }) {
  const alive = useRef(true);
  const [saved, setSaved] = useState<Record<string, string | null>>({});
  const [changed, setChanged] = useState(0);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const request = useCallback(async <T,>(path = "", options: RequestInit = {}): Promise<T> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!alive.current || !userId || session?.user.id !== userId) throw new DOMException("Session changed", "AbortError");
    const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/notes${path}`, {
      ...options, headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}`, ...options.headers },
    });
    const payload = response.status === 204 ? undefined : await response.json();
    if (!alive.current) throw new DOMException("Session changed", "AbortError");
    if (!response.ok) throw new NotesError(response.status, payload?.error || "Could not load notes. Please retry.");
    return payload as T;
  }, [userId]);
  const remember = useCallback((note: Note) => {
    if (!alive.current) return;
    if (note.source_message_id) setSaved(prev => ({ ...prev, [note.source_message_id!]: note.id }));
    setChanged(n => n + 1);
  }, []);
  const forget = useCallback((messageId: string) => {
    if (!alive.current) return;
    setSaved(prev => ({ ...prev, [messageId]: null }));
    setChanged(n => n + 1);
  }, []);
  const remove = useCallback(async (note: Note) => {
    await request(`/${note.id}`, { method: "DELETE" });
    if (note.source_message_id) forget(note.source_message_id);
    else setChanged(n => n + 1);
  }, [request, forget]);
  return <NotesContext.Provider value={{ request, saved, changed, remember, forget, remove }}>{children}</NotesContext.Provider>;
}
export function NotesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  return <SessionNotes key={user?.id || "guest"} userId={user?.id}>{children}</SessionNotes>;
}
